import React, { useEffect, useRef, useState } from "react";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { useParams, useNavigate } from "react-router-dom";
import QuestionRenderer from "./QuestionRenderer";
import { serverTimestamp } from "firebase/firestore";

export default function ReadingMock() {
  const { mockId, passageId, questionId } = useParams();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [showPassage, setShowPassage] = useState(false);
  const focusRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`/data/${mockId}Reading${passageId}.json`)
      .then((res) => res.json())
      .then(setData)
      .catch((err) => console.error("Failed to load mock data:", err));
  }, [mockId, passageId]);

  useEffect(() => {
    setShowPassage(false);
  }, [questionId]);

  useEffect(() => {
    if (showPassage && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showPassage]);

  // ✅ Wait until data is loaded
  if (!data) return <div>Loading...</div>;

  // ✅ Calculate questionIndex after data is loaded
  const questionIndex = data.questions.findIndex((q) => String(q.id) === String(questionId));

  if (questionIndex === -1) {
    console.warn(`❌ Question ID '${questionId}' not found in questions:`, data.questions.map(q => q.id));
    return <div>Question not found. Please check your data IDs.</div>;
  }
  


  const question = data.questions[questionIndex];
  const passageParagraphs = data.passage;

  const isLastQuestion = questionIndex === data.questions.length - 1;
  const isFirstQuestion = questionIndex === 0;

  const handleNext = () => {
    if (questionIndex < data.questions.length - 1) {
      const nextId = data.questions[questionIndex + 1].id;
      navigate(`/lessonMock/${mockId}/reading/${passageId}/${nextId}`);
    }
  };

  const handlePrev = () => {
    if (questionIndex > 0) {
      const prevId = data.questions[questionIndex - 1].id;
      navigate(`/lessonMock/${mockId}/reading/${passageId}/${prevId}`);
    }
  };

  const handleSubmit = () => {
    if (passageId === "1") {
      navigate(`/lessonMock/${mockId}/reading2`);
    } else {
      navigate(`/lessonMock/${mockId}/listening/1/audio`);
    }
  };
  

  const handleAnswer = async (value) => {
    const updatedAnswers = { ...answers, [question.id]: value };
    setAnswers(updatedAnswers);

    const user = auth.currentUser;
    const userId = user?.uid;

    if (!user) return;

    const docRef = doc(db, "users", userId, "mockAnswers", `${mockId}_reading${passageId}`);
    await setDoc(
      docRef,
      {
        ...updatedAnswers,
        email: user.email,
        name: user.displayName,
        role: "student",
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
    
  };

  const renderPassageWithFocus = (passage, focus) => {
    return passage.map((para, index) => {
      if (focus?.type === "paragraph" && index === focus.paragraph) {
        return (
          <p key={index} ref={focusRef} className="font-bold">
            {para}
          </p>
        );
      }

      if (focus?.type === "paragraphs" && Array.isArray(focus.paragraphs)) {
        const ref =
          focus.paragraphs.includes(index) && focus.paragraphs[0] === index
            ? focusRef
            : null;
        if (focus.paragraphs.includes(index)) {
          return (
            <p key={index} ref={ref} className="font-bold">
              {para}
            </p>
          );
        }
      }

      if (focus?.type === "word" && index === focus.paragraph) {
        const parts = para.split(new RegExp(`(${focus.word})`, "i"));
        return (
          <p key={index} ref={focusRef}>
            {parts.map((part, i) =>
              part.toLowerCase() === focus.word.toLowerCase() ? (
                <mark key={i} className="bg-yellow-200">
                  {part}
                </mark>
              ) : (
                part
              )
            )}
          </p>
        );
      }

      if (
        (focus?.type === "sentence" || focus?.type === "phrase") &&
        index === focus.paragraph
      ) {
        if (!para.includes(focus.text)) return <p key={index}>{para}</p>;
        const parts = para.split(focus.text);
        return (
          <p key={index} ref={focusRef}>
            {parts[0]}
            <mark className="bg-yellow-200 font-medium">{focus.text}</mark>
            {parts[1]}
          </p>
        );
      }

      return <p key={index}>{para}</p>;
    });
  };

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <h2 className="text-xl font-bold mb-4">Reading Passage {passageId}</h2>
      <button
        onClick={() => setShowPassage(!showPassage)}
        className="mb-4 px-4 py-2 rounded bg-darkblue-100 hover:bg-gray-200 border border-gray-300"
      >
        {showPassage ? "Hide Passage" : "Show Passage"}
      </button>

      {showPassage && (
        <div className="mb-6 space-y-2">
          {renderPassageWithFocus(passageParagraphs, question.focus)}
        </div>
      )}

<h3 className="text-lg font-semibold mb-2">Question {questionIndex + 1}</h3>
      <QuestionRenderer
        key={question.id}
        question={question}
        userAnswer={answers[question.id]}
        onAnswer={handleAnswer}
      />

      <div className="mt-8 flex justify-between items-center">
        {!isFirstQuestion && (
          <button
            onClick={handlePrev}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-md shadow"
          >
            ← Previous
          </button>
        )}

        {!isLastQuestion ? (
          <button
            onClick={handleNext}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md shadow ml-auto"
          >
            Next Question →
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md shadow ml-auto"
          >
            Submit Reading Section
          </button>
        )}
      </div>
    </div>
  );
}
